import { logger } from './logger';

export const networkDiagnostics = {
  async testSupabaseConnection(): Promise<{
    url: string;
    reachable: boolean;
    error?: string;
    dns?: string;
    suggestion?: string;
  }> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    
    if (!supabaseUrl) {
      return {
        url: 'Not configured',
        reachable: false,
        error: 'VITE_SUPABASE_URL is not set in environment variables',
        suggestion: 'Check your .env file and ensure VITE_SUPABASE_URL is set'
      };
    }

    try {
      // Try to fetch from Supabase health endpoint
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${supabaseUrl}/rest/v1/`, {
        method: 'HEAD',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      return {
        url: supabaseUrl,
        reachable: true,
        dns: 'Resolved successfully'
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      let suggestion = 'Unknown issue';
      
      if (errorMessage.includes('ERR_NAME_NOT_RESOLVED')) {
        suggestion = 'DNS resolution failed. Try: 1) Clear DNS cache (ipconfig /flushdns), 2) Change DNS to 8.8.8.8, 3) Check VPN/firewall settings';
      } else if (errorMessage.includes('ERR_NETWORK_CHANGED')) {
        suggestion = 'Network changed during request. Check your internet connection stability';
      } else if (errorMessage.includes('ERR_INTERNET_DISCONNECTED')) {
        suggestion = 'No internet connection detected';
      } else if (errorMessage.includes('AbortError')) {
        suggestion = 'Request timed out. Supabase might be slow or blocked by firewall';
      }
      
      return {
        url: supabaseUrl,
        reachable: false,
        error: errorMessage,
        suggestion
      };
    }
  },

  async diagnoseAndLog(): Promise<{
    url: string;
    reachable: boolean;
    error?: string;
    dns?: string;
    suggestion?: string;
  }> {
    logger.info('=== Running Supabase Network Diagnostics ===');
    
    const result = await this.testSupabaseConnection();
    
    logger.info('Supabase URL:', result.url);
    logger.info('Reachable:', result.reachable);
    
    if (!result.reachable) {
      logger.error('Connection Error:', result.error);
      logger.warn('Suggestion:', result.suggestion);
      
      // Additional diagnostics
      if (typeof window !== 'undefined') {
        logger.info('Browser:', navigator.userAgent);
        logger.info('Online:', navigator.onLine);
      }
    } else {
      logger.info('Connection successful!');
    }
    
    logger.info('=== End Diagnostics ===');
    
    // Return result for UI display if needed
    return result;
  }
};

// Auto-run diagnostics if there's a connection error
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.error?.message?.includes('ERR_NAME_NOT_RESOLVED') && 
        event.error?.message?.includes('supabase')) {
      logger.error('Supabase DNS error detected, running diagnostics...');
      networkDiagnostics.diagnoseAndLog();
    }
  });
  
  // Add global function for manual diagnostics
  (window as any).diagnoseSupabase = () => networkDiagnostics.diagnoseAndLog();
} 